import { get, keys, last } from 'lodash-es';

import makeChartConfig from '../../../../../components/Chart/LineChart';
import { format, formatQuarterYear, today, yesterday, differenceInDays } from '../../../../../utils/date';
import { numberToHumanChart, numberToPercentage } from '../../../../../utils/formatter';
import { ADVANCED_METRICS_PERCENTAGE } from '../../../../../utils/advanced-metrics';
import { getFallbackSubDateByGranularity } from '../../../../../utils/helpers';

const unitsByMetric = ADVANCED_METRICS_PERCENTAGE.reduce((acc, item) => ({ ...acc, [item]: '%' }), {});

export const formatByGranularity = (date, granularity) => {
  if (granularity === 'weekly') return format(date, 'MM/DD/YY');
  if (granularity === 'monthly') return format(date, 'MM/YY');
  return formatQuarterYear(date);
};

export const formatToDatePeriod = (date, toDatePeriod) => {
  if (toDatePeriod === 'year') return format(date, 'YYYY');
  return formatQuarterYear(date, { yearFormat: 'YYYY' });
};

export const chartCohortsYearConfig = (series, {
  granularity,
  overPeriod,
  rotateLabels,
  height,
  animation = {
    duration: 300,
  },
  yGridLine,
  yAxisZeroMiddle,
  variant,
  maxDate,
  subDates,
}) => {
  const firstSeriesKey = keys(series)[0];
  const firstSeries = get(series, [firstSeriesKey], []);

  const startPartialDate = 1;
  const endPartialDate = firstSeries.length > 0
    ? differenceInDays(maxDate || yesterday(), last(firstSeries)[0]) + 1
    : startPartialDate;

  // const xTickPositions = firstSeries.map(value => value[0]);

  const spacingByVariant = {
    qv: [10, 0, 0, 0],
    advanced: [10, 0, 0, 0],
  };

  const xAxisLabelOffsetByVariant = {
    qv: 34,
    advanced: 34,
  };

  return makeChartConfig({
    chartOptions: {
      type: 'spline',
      spacing: spacingByVariant[variant],
      height,
      animation,
    },
    yAxis: {
      title: null,
      tickAmount: 6,
      startOnTick: true,
      labels: {
        reserveSpace: true,
        y: 5,
        style: {
          color: '#848EA1',
          fontSize: 12,
        },
        formatter() {
          return `${this.value}%`;
        },
      },
      ...(yGridLine ? {
        gridLineWidth: 1,
        gridLineColor: '#EDF0F5',
      } : {}),
      ...(yAxisZeroMiddle ? {
        tickPositioner() {
          const maxDeviation = Math.ceil(Math.max(Math.abs(this.dataMax), Math.abs(this.dataMin)));
          const halfMaxDeviation = Math.ceil(maxDeviation / 2);

          return [-maxDeviation, -halfMaxDeviation, 0, halfMaxDeviation, maxDeviation];
        },
      } : {}),
    },
    xAxisOptions: {
      tickLength: 0,
      lineWidth: 0,
      crosshair: false,
      minPadding: 0,
      // tickPositions: xTickPositions.length > 0 ? xTickPositions : null,
    },
    xAxisLabels: {
      formatter() {
        return `<span>${formatByGranularity(this.value, granularity)}</span>`;
      },
      style: {
        color: '#848EA1',
        fontSize: 12,
      },
      showFirstLabel: true,
      autoRotation: true,
      y: xAxisLabelOffsetByVariant[variant],
      ...(rotateLabels ? { rotation: -45 } : {}),
    },
    tooltipOptions: {
      borderWidth: 0,
      padding: 0,
      visible: true,
      shared: true,
      valueDecimals: 2,
      useHTML: true,
      outside: true,
      className: 'quick-view-tooltip',
      formatter() {
        let rows;

        const template = point => (
          `<tr>
            <td class="text-left text-black text-14 p-l-0 p-b-0 pr-md">
              <div class="text-truncate text-black text-14" style='max-width: 100%'>
                <span class="tooltip-bubble-marker" style="background-color: ${point.series.color};border-color: ${point.series.color};"></span>
                <span>${point.series.name}</span>
              </div>
            </td>
              <td class="text-right text-bold text-black text-14 p-l-0 p-b-0" style="line-height:150%">
                <span>${numberToPercentage(point.y)}</span>
              </td>
          </tr>`
        );

        if (Array.isArray(this.points)) {
          rows = this.points.map(point => template(point));
        } else return false;

        const isPartialElem = this.points[0].point.options.isPartial;

        const currentDate = formatByGranularity(this.x, granularity);
        let subDate;

        if (subDates?.[this.x]) {
          subDate = subDates[this.x];
        } else { // Fallback
          subDate = getFallbackSubDateByGranularity({
            isOverPeriod: overPeriod === 'period_over_period',
            granularity,
            date: this.x,
          });
        }
        const prevDate = formatByGranularity(subDate, granularity);

        return `
          <div
            style="
              min-width: 170px;
              border-radius: 8px;
              border: 1px solid #E1E5EB;
              padding: 12px 16px;
              background: #fff;
              box-shadow: 0px 0px 24px 0px rgba(0, 0, 0, 0.08);
            "
          >
            <table class="table">
              <tr>
                <td class="text-left text-black text-12 p-l-0 p-b-0 pr-md" colspan="2">
                  ${currentDate} vs ${prevDate}
                </td>
              </tr>
              <tr style=display:${!isPartialElem && 'none'}>
                <td class="text-left text-black text-12 p-l-0 p-b-0 pr-md" colspan="2">
                  Days ${endPartialDate === 0 ? `${startPartialDate}` : `${startPartialDate} - ${endPartialDate}`}
                </td>
              </tr>
              <tr>${rows.join('')}</tr>
            </table>
          </div>
        `;
      },
    },
    plotOptionsSeries: {
      animation,
    },
    plotOptionsSettings: {
      column: {
        minPointLength: 5,
        groupPadding: 0.12,
        pointPadding: 0.03,
        borderRadius: 0,
      },
    },
  });
};

const postfixByMetric = {
  ...unitsByMetric,
  average_time_spent_per_dau: ' minutes',
};

export const chartCohortsQuarterConfig = ({
  metric,
  toDatePeriod,
  labelStep,
  height,
  animation = {
    duration: 300,
  },
  floor,
  ceiling,
  variant,
  maxDate,
}) => {
  const tickAmountByVariant = {
    advanced: null,
    advancedMinimized: null,
  };

  const currPeriod = formatToDatePeriod(maxDate || today(), toDatePeriod);
  let prevPeriod;

  if (toDatePeriod === 'year') {
    prevPeriod = `${currPeriod - 1}`;
  } else {
    const [quarter, year] = currPeriod.split('Q');
    prevPeriod = `${quarter}Q${year - 1}`;
  }

  return makeChartConfig({
    chartOptions: {
      type: 'spline',
      spacing: [10, 6, 0, 6],
      height,
      animation,
    },
    yAxis: {
      title: null,
      floor,
      ceiling,
      minPadding: 0.01,
      maxPadding: 0.01,
      startOnTick: true,
      tickAmount: tickAmountByVariant[variant],
      labels: {
        reserveSpace: true,
        style: {
          color: '#848EA1',
          fontSize: 12,
        },
        formatter() {
          return `${numberToHumanChart(this.value)}${unitsByMetric[metric] || ''}`;
        },
      },
    },
    xAxisOptions: {
      crosshair: false,
      lineWidth: 0,
      tickLength: 0,
      type: 'category',
    },
    xAxisLabels: {
      formatter() {
        return `${this.isFirst ? 'Day ' : ''}${this.value}`;
      },
      style: {
        color: '#848EA1',
        fontSize: 12,
      },
      rotation: 0,
      step: labelStep,
      y: 30,
    },
    tooltipOptions: {
      borderWidth: 0,
      padding: 0,
      shared: true,
      valueDecimals: 2,
      useHTML: true,
      outside: true,
      className: 'quick-view-tooltip',
      formatter() {
        const date = `Day ${this.x}`;
        let rows;

        const template = point => (
          `<tr>
            <td class="text-left text-black text-14 p-l-0 p-b-0 pr-md">
              <div class="text-truncate text-black text-14" style='max-width: 100%'>
                <span
                  class="tooltip-bubble-marker"
                  style="
                    background-color: ${point.color};
                    border-color: ${point.color};"
                  >
                </span>
                <span>${point.isStub ? point.date : formatToDatePeriod(point.point.date, toDatePeriod)}</span>
              </div>
            </td>
            <td class="text-right text-black text-14 p-l-0 p-b-0">
              ${point.isStub ? '—' : `${numberToHumanChart(point.y)}${postfixByMetric[metric] || ''}`}
            </td>
          </tr>`
        );

        if (Array.isArray(this.points)) {
          let { points } = this;

          if (points.length === 1) {
            const pointDate = formatToDatePeriod(points[0].point?.date, toDatePeriod);

            if (pointDate === prevPeriod) {
              points = [{ isStub: true, color: '#348BE3', date: currPeriod }, ...this.points];
            } else if (pointDate === currPeriod) {
              points = [...this.points, { isStub: true, color: '#E1E5EB', date: prevPeriod }];
            }
          }

          rows = points.map(point => template(point));
        } else return false;

        return `
          <div
            style="
              min-width: 189px;
              border-radius: 8px;
              border: 1px solid #E1E5EB;
              padding: 16px;
              background: #fff;
              box-shadow: 0px 0px 24px 0px rgba(0, 0, 0, 0.08);
            "
          >
            <div style="
            font-size: 12px;
            font-style: normal;
            font-weight: 400;
            line-height: 150%;
            color: #848EA1;
            margin-bottom: 8px"
            >
            ${date}
            </div>
            <table class="table">${rows.join('')}</table>
          </div>
        `;
      },
    },
    plotOptionsSeries: {
      animation,
    },
  });
};
